import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";
import { cachePaymentMethods, getCachedPaymentMethods } from "@/lib/pdv-cache";
import { getErrorMessage, logError } from "@/lib/error-messages";
import { getPaymentMethods } from "@/lib/dataGateway";

export type PaymentMethodType = "cash" | "card" | "digital_wallet";

export interface PaymentMethod {
  id: string;
  restaurant_id: string;
  name: string;
  type: PaymentMethodType;
  fee_percentage: number;
  receiving_days: number;
  is_active: boolean;
  is_system_default: boolean;
  chart_account_id: string | null;
  fiscal_code: string | null;
  icon_color: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentMethodFormData {
  name: string;
  type: PaymentMethodType;
  fee_percentage: number;
  receiving_days: number;
  is_active: boolean;
  chart_account_id?: string | null;
  icon_color?: string | null;
}

export function usePaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { activeRestaurant } = useRestaurantContext();
  const { logOperation } = useOperationLogs();

  const fetchPaymentMethods = useCallback(async () => {
    if (!activeRestaurant) {
      setPaymentMethods([]);
      setIsLoading(false);
      return;
    }

    // CACHE FIRST - carregar instantaneamente do cache
    try {
      const cached = await getCachedPaymentMethods<PaymentMethod>(activeRestaurant.id);
      if (cached && cached.length > 0) {
        setPaymentMethods(cached);
        setIsLoading(false); // UI pronta imediatamente
        
        // STALE-WHILE-REVALIDATE - atualizar silenciosamente em background.
        // Via DataGateway: tenta servidor local primeiro, cai para Supabase.
        if (navigator.onLine) {
          getPaymentMethods<PaymentMethod>(activeRestaurant.id).then(({ data, error, source }) => {
            if (!error && data) {
              setPaymentMethods(data);
              cachePaymentMethods(activeRestaurant.id, data); // Fire-and-forget
              if (import.meta.env.DEV) {
                console.debug(`[usePaymentMethods] fonte: ${source}`);
              }
            }
            // Se erro de rede, mantém cache (silencioso)
          });
        }
        return;
      }
    } catch {
      // Cache error - continue to network
    }

    // SEM CACHE - aguardar rede
    setIsLoading(true);
    
    if (navigator.onLine) {
      // Via DataGateway: tenta servidor local primeiro, cai para Supabase.
      const { data, error, source } = await getPaymentMethods<PaymentMethod>(activeRestaurant.id);

      if (error) {
        toast.error("Erro ao carregar formas de pagamento");
      } else {
        setPaymentMethods(data || []);
        cachePaymentMethods(activeRestaurant.id, data || []); // Fire-and-forget
        if (import.meta.env.DEV) {
          console.debug(`[usePaymentMethods] fonte: ${source}`);
        }
      }
    } else {
      toast.error("Sem conexão e sem cache de pagamentos");
    }
    
    setIsLoading(false);
  }, [activeRestaurant]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const createPaymentMethod = async (formData: PaymentMethodFormData) => {
    if (!activeRestaurant) return null;

    const { data, error } = await supabase
      .from("payment_methods")
      .insert({
        restaurant_id: activeRestaurant.id,
        name: formData.name,
        type: formData.type,
        fee_percentage: formData.fee_percentage,
        receiving_days: formData.receiving_days,
        is_active: formData.is_active,
        is_system_default: false,
      })
      .select()
      .single();

    if (error) {
      logError(error, "usePaymentMethods.createPaymentMethod");
      toast.error(getErrorMessage(error, "criar", "payment_method"));
      return null;
    }

    await logOperation("create", "payment_method", data.id, {
      name: data.name,
      type: data.type,
    });

    toast.success("Forma de pagamento criada com sucesso!");
    await fetchPaymentMethods();
    return data;
  };

  const updatePaymentMethod = async (id: string, formData: Partial<PaymentMethodFormData> & { fiscal_code?: string | null }) => {
    const { error } = await supabase
      .from("payment_methods")
      .update({
        name: formData.name,
        type: formData.type,
        fee_percentage: formData.fee_percentage,
        receiving_days: formData.receiving_days,
        is_active: formData.is_active,
        chart_account_id: formData.chart_account_id ?? null,
        fiscal_code: formData.fiscal_code ?? null,
        icon_color: formData.icon_color ?? null,
      })
      .eq("id", id);

    if (error) {
      logError(error, "usePaymentMethods.updatePaymentMethod");
      toast.error(getErrorMessage(error, "atualizar", "payment_method"));
      return false;
    }

    toast.success("Forma de pagamento atualizada!");
    await fetchPaymentMethods();
    return true;
  };

  const deletePaymentMethod = async (id: string) => {
    const method = paymentMethods.find((m) => m.id === id);
    if (method?.is_system_default) {
      toast.error("Não é possível excluir métodos padrão do sistema");
      return false;
    }

    const { error } = await supabase
      .from("payment_methods")
      .delete()
      .eq("id", id);

    if (error) {
      logError(error, "usePaymentMethods.deletePaymentMethod");
      toast.error(getErrorMessage(error, "excluir", "payment_method"));
      return false;
    }

    toast.success("Forma de pagamento excluída!");
    await fetchPaymentMethods();
    return true;
  };

  const toggleActive = async (id: string) => {
    const method = paymentMethods.find((m) => m.id === id);
    if (!method) return false;

    return updatePaymentMethod(id, { is_active: !method.is_active });
  };

  const getActivePaymentMethods = () => {
    return paymentMethods.filter((m) => m.is_active);
  };

  return {
    paymentMethods,
    isLoading,
    fetchPaymentMethods,
    createPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod,
    toggleActive,
    getActivePaymentMethods,
  };
}
